import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, Loader2, ShieldCheck, AlertTriangle, X, Eye, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, money, downloadBlob } from "@/lib/api";
import { FddReview } from "@/components/FddReview";

const upload = async (file, onProgress) => {
  const { data: init } = await api.post("/fdd/uploads", { filename: file.name, size: file.size });
  for (let i = 0, n = 0; i < file.size; i += init.chunk_size, n++) {
    await api.put(`/fdd/uploads/${init.id}/chunk`, file.slice(i, i + init.chunk_size), { params: { index: n }, headers: { "Content-Type": "application/octet-stream" } });
    onProgress(Math.round(Math.min(1, (i + init.chunk_size) / file.size) * 100));
  }
  await api.post(`/fdd/uploads/${init.id}/complete`);
  return init.id;
};

const Applied = ({ doc, testId, onReview, onClear }) => {
  const f = doc.figures || {};
  return (
    <div className="flex flex-wrap items-center justify-between gap-3" data-testid={`${testId}-applied`}>
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-300"><ShieldCheck className="h-4 w-4" />Verified from FDD{!doc.reviewed && <span className="text-amber-300 font-normal">· not reviewed</span>}</p>
        <p className="text-xs text-slate-400 truncate">{doc.filename} · Item 7: {money(f.investment_low)} – {money(f.investment_high)} · Item 19 avg: {money(f.item19?.avg_revenue)} · Item 20 units: {f.item20?.total_units ?? "—"}</p>
      </div>
      <div className="flex gap-1">
        <Button type="button" size="sm" variant="ghost" data-testid={`${testId}-view`} onClick={() => downloadBlob(`/fdd/${doc.id}/file`, doc.filename).catch(() => toast.error("Could not open the FDD"))}><Eye className="h-4 w-4 mr-1" />PDF</Button>
        <Button type="button" size="sm" variant="ghost" data-testid={`${testId}-edit`} onClick={onReview}><Pencil className="h-4 w-4 mr-1" />Review</Button>
        <Button type="button" size="sm" variant="ghost" data-testid={`${testId}-remove`} onClick={onClear} className="hover:text-rose-400"><X className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

export const FddUpload = ({ fddId, onApply, onClear, testId = "fdd" }) => {
  const [doc, setDoc] = useState(null);
  const [progress, setProgress] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const input = useRef(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { if (fddId && doc?.id !== fddId) api.get(`/fdd/${fddId}`).then((r) => setDoc(r.data)).catch(() => {}); }, [fddId]); // eslint-disable-line react-hooks/exhaustive-deps

  const poll = async (id) => {
    while (alive.current) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data } = await api.get(`/fdd/${id}`).catch(() => ({ data: { status: "processing" } }));
      if (data.status === "processing") continue;
      setDoc(data);
      if (data.status === "ready") setReviewing(true); else toast.error(data.error || "Could not read this FDD");
      return;
    }
  };
  const pick = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Please choose a PDF file");
    setProgress(0);
    try { const id = await upload(file, setProgress); setDoc({ id, filename: file.name, status: "processing" }); poll(id); }
    catch (e) { toast.error(errMsg(e, "Upload failed")); } finally { setProgress(null); if (input.current) input.current.value = ""; }
  };
  const clear = () => { setDoc(null); setReviewing(false); onClear(); };

  let body;
  if (progress !== null) body = <div data-testid={`${testId}-uploading`}><p className="text-sm text-slate-300 mb-2">Uploading FDD… {progress}%</p><div className="h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${progress}%` }} /></div></div>;
  else if (doc?.status === "processing") body = <p className="flex items-center gap-2 text-sm text-slate-300" data-testid={`${testId}-processing`}><Loader2 className="h-4 w-4 animate-spin text-emerald-400" />Reading the FDD and pulling Items 5-7, 19 and 20. This takes about a minute.</p>;
  else if (doc?.status === "ready" && reviewing) body = <FddReview doc={doc} testId={testId} onSaved={(d) => { setDoc(d); setReviewing(false); onApply(d); }} onCancel={fddId === doc.id ? () => setReviewing(false) : clear} />;
  else if (doc?.status === "ready" && fddId === doc.id) body = <Applied doc={doc} testId={testId} onReview={() => setReviewing(true)} onClear={clear} />;
  else body = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-sm font-semibold text-slate-200">Have the brand's FDD?</p>
        <p className="text-xs text-slate-400">Upload the PDF to use real Item 7, 19 and 20 figures instead of estimates.</p>
        {doc?.status === "failed" && <p className="flex items-center gap-1 text-xs text-rose-300 mt-1" data-testid={`${testId}-error`}><AlertTriangle className="h-3.5 w-3.5" />{doc.error}</p>}
      </div>
      <Button type="button" size="sm" variant="outline" data-testid={`${testId}-upload-btn`} onClick={() => input.current?.click()} className="rounded-full border-emerald-500/40 bg-transparent text-emerald-300 hover:bg-emerald-500/10"><FileUp className="h-4 w-4 mr-1" />Upload FDD (PDF)</Button>
    </div>
  );
  return (
    <div className="rounded-lg border border-dashed border-slate-700 bg-slate-950/40 p-4" data-testid={testId}>
      <input ref={input} type="file" accept="application/pdf,.pdf" className="hidden" data-testid={`${testId}-file-input`} onChange={(e) => pick(e.target.files?.[0])} />
      {body}
    </div>
  );
};
